import { useErrorReporter } from '@/composables/useErrorReporter'
import { API_BASE } from '@/constants'
import { SAFE_ERROR_MESSAGES, SAFE_ERROR_TITLES } from '@/constants/api/error-codes'
import { createAccountDiscountApi } from '@/helpers/api-client/account-discounts'
import { createAddAccountApi } from '@/helpers/api-client/add-account'
import { createDeviceToolsApi } from '@/helpers/api-client/device-tools'
import { createLeadsApi } from '@/helpers/api-client/leads'
import { createInternalDeviceToolsApi } from '@/helpers/api-client/internal-device-tools'
import { createOnboardingV2Api } from '@/helpers/api-client/onboarding-v2'
import { createPregeneratedFeesApi } from '@/helpers/api-client/pregenerated-fees'
import { createSuperAdminPermissionsApi } from '@/helpers/api-client/super-admin-permissions'
import { createSuperAdminRolesApi } from '@/helpers/api-client/super-admin-roles'
import { createSuperAdminUsersApi } from '@/helpers/api-client/super-admin-users'

import type {
	ApiError,
	ApiErrorAllowed,
	ApiErrorWithCode,
	ApiState,
	ExtendedFetchOptions
} from '@/types/api-client'
import type { FetchOptions } from 'ofetch'
import { $fetch } from 'ofetch'

/**
 * Result of pattern matching with captured variables
 */
interface PatternMatch {
	matched: boolean
	variables: Record<string, string>
}

/**
 * Check if an error message matches a pattern and extract variables
 * Supports three pattern types:
 * 1. Named captures: {variableName} - Captures value and passes to i18n
 * 2. Wildcards: * - Matches any characters but doesn't capture
 * 3. Exact match - Case-insensitive exact string match
 *
 * @param pattern - The pattern to match (e.g., "Account {accountId} was not found")
 * @param message - The actual error message from API
 * @returns Object with matched boolean and captured variables
 * @example
 * // Named capture
 * matchesPattern("Account {accountId} was not found", "Account 123 was not found")
 * // Returns: { matched: true, variables: { accountId: "123" } }
 *
 * // Wildcard
 * matchesPattern("Account * was not found", "Account 123 was not found")
 * // Returns: { matched: true, variables: {} }
 *
 * // Exact match
 * matchesPattern("exact match", "exact match")
 * // Returns: { matched: true, variables: {} }
 */
const matchesPattern = (pattern: string, message: string): PatternMatch => {
	// Case 1: Pattern has named captures {variableName}
	if (pattern.includes('{') && pattern.includes('}')) {
		// Escape special regex characters EXCEPT { and }
		let regexPattern = pattern.replace(/[.+?^$()|[\]\\]/g, '\\$&')

		// Convert {variableName} to named capture group (?<variableName>.+?)
		regexPattern = regexPattern.replace(/\{(\w+)\}/g, '(?<$1>.+?)')

		const regex = new RegExp(`^${regexPattern}$`, 'i')
		const match = regex.exec(message)

		if (match && match.groups) {
			return {
				matched: true,
				variables: match.groups as Record<string, string>
			}
		}

		return { matched: false, variables: {} }
	}

	// Case 2: Pattern has wildcards * (no variable capture)
	if (pattern.includes('*')) {
		// Escape special regex chars except *, then replace * with .*
		const regexPattern = pattern
			.replace(/[.+?^${}()|[\]\\]/g, '\\$&') // Escape special chars
			.replace(/\*/g, '.*') // Replace * with .*

		const regex = new RegExp(`^${regexPattern}$`, 'i')
		return {
			matched: regex.test(message),
			variables: {}
		}
	}

	// Case 3: Exact match
	return {
		matched: pattern.toLowerCase() === message.toLowerCase(),
		variables: {}
	}
}

// Custom fetch instance with error handling
class ApiClient {
	private baseURL: string

	constructor(baseURL: string) {
		this.baseURL = baseURL
	}

	// Create enhanced error with API error structure
	private createApiError(errorData: ApiError): ApiErrorWithCode {
		const firstError = errorData?.errors?.[0]
		const errorMessage = errorData?.error || firstError?.detail || 'An error occurred'

		const error = new Error(errorMessage) as ApiErrorWithCode

		// Attach API error information for enhanced handling
		if (firstError) {
			error.id = firstError.id
			error.status = firstError.status
			error.title = firstError.title
		}

		return error
	}

	/**
	 * Handle automatic error reporting and user-friendly message mapping.
	 * @description This function processes API errors using separate title and message mapping strategies:
	 *
	 * **Title Processing:**
	 * 1. **Single title override** - If options.title has only one entry, use it for ANY error
	 * 2. **Custom title mapping** - Maps API error titles to i18n keys via options.title
	 * 3. **Single API error title** - Uses title from single error response directly
	 * 4. **Whitelisted titles** - Uses API error title if in SAFE_ERROR_TITLES whitelist
	 * 5. **Generic title fallback** - "Error" (i18n: errors.generic_title)
	 *
	 * **Message Processing:**
	 * 1. **Single message override** - If options.message has only one entry, use it for ANY error
	 * 2. **Custom message mapping** - Maps API error messages to i18n keys via options.message
	 * 3. **Whitelisted messages** - Uses API error message if in SAFE_ERROR_MESSAGES whitelist
	 * 4. **API error detail** - Uses raw detail field from API response
	 * 5. **Generic message fallback** - "An error occurred"
	 *
	 * All messages go through i18n translation when available, with fallback to raw strings.
	 * All provided error mappings must use i18n key format (e.g. "errors.not_found").
	 *
	 * @param error - The API error object
	 * @param options - Optional error mappings with separate title and message Record objects
	 * @param options.title - Maps API error titles to i18n keys
	 * @param options.message - Maps API error messages to i18n keys
	 */
	private handleAutoError(error: unknown, errorsAllowed?: ApiErrorAllowed): void {
		const { bugsnagReport } = useErrorReporter()

		const apiError = error as ApiErrorWithCode

		let message = apiError.message || 'An error occurred'
		let title = 'Error'

		// Check if we can get the original error response to check for single error
		const originalError = (error as any).data as ApiError | undefined
		const hasSingleError = originalError?.errors?.length === 1

		// Get i18n function
		const { $i18n } = useNuxtApp()
		const t = $i18n.t

		// ==============================================
		// SECTION 1: TITLE MAPPING AND FALLBACKS
		// ==============================================

		// Set default title
		title = t('errors.generic_title')

		// Single title override strategy (highest priority)
		if (errorsAllowed?.titles && Object.keys(errorsAllowed.titles).length === 1) {
			const errorOverride = Object.values(errorsAllowed.titles)[0]
			title = t(errorOverride)
		} else if (errorsAllowed?.titles && apiError.title) { // Check for custom title mapping
			const customTitleMapping = Object.entries(errorsAllowed.titles).find(([ errorKey ]) =>
				matchesPattern(errorKey, apiError.title || '').matched
			)?.[1]

			if (customTitleMapping) {
				const errorKey = Object.entries(errorsAllowed.titles).find(([ key ]) =>
				matchesPattern(key, apiError.title || '').matched
			)?.[0] || ''
			const { variables } = matchesPattern(errorKey, apiError.title || '')
			title = t(customTitleMapping, variables)
			}
		} else if (hasSingleError && originalError?.errors?.[0]?.title) { // Single API error title fallback
			title = originalError.errors[0].title
		} else if (apiError.title && SAFE_ERROR_TITLES.includes(apiError.title)) { // Whitelisted API error title fallback (no translation)
			title = apiError.title
		}

		// ==============================================
		// SECTION 2: MESSAGE MAPPING AND FALLBACKS
		// ==============================================

		// Set default message
		message = t('errors.generic_message')

		// Single message override strategy (highest priority)
		if (errorsAllowed?.messages && Object.keys(errorsAllowed.messages).length === 1) {
			const errorOverride = Object.values(errorsAllowed.messages)[0]
			message = t(errorOverride)
		} else if (errorsAllowed?.messages && apiError.message) { // Custom error message mapping
			const customMessageMapping = Object.entries(errorsAllowed.messages).find(([ errorKey ]) =>
				matchesPattern(errorKey, apiError.message || '').matched
			)?.[1]

			if (customMessageMapping) {
				const errorKey = Object.entries(errorsAllowed.messages).find(([ key ]) =>
				matchesPattern(key, apiError.message || '').matched
			)?.[0] || ''
			const { variables } = matchesPattern(errorKey, apiError.message || '')
			message = t(customMessageMapping, variables)
			}
		} else if (apiError.message && SAFE_ERROR_MESSAGES.includes(apiError.message)) { // Whitelisted API error message fallback (no translation)
			message = apiError.message
		}

		// Send original error to Bugsnag, display toast notification with sanitized title & message
		bugsnagReport({
			error: apiError,
			showToast: true,
			toast: {
				title,
				message
			}
		})
	}

	// Unified request method with dynamic error handling based on payload or options
	private async request<T>(
		endpoint: string,
		options: ExtendedFetchOptions = {}
	): Promise<T | null> {
		const {
			autoError = true,
			errorsAllowed,
			...fetchOptions
		} = options

		const url = `${this.baseURL}${endpoint}`

		const defaultOptions: FetchOptions<'json' | any> = {
			credentials: 'include',
			onResponseError: ({ response }) => {
				const errorData = response._data as ApiError
				throw this.createApiError(errorData)
			},
			...fetchOptions
		}

		try {
			return await $fetch<T>(url, defaultOptions)
		} catch (error) {
			if (autoError) {
				this.handleAutoError(error, errorsAllowed)
				return null // Auto-error returns null
			} else {
				throw error // Let caller handle the error
			}
		}
	}

	// Type-safe API reactive state creation with error handling built into the base request onResponseError hook
	private createApiState<T>(
		endpoint: string,
		options: ExtendedFetchOptions = {}
	): ApiState<T> {
		const data = ref<T | null>(null) as Ref<T | null>
		const error = ref<Error | null>(null)
		const pending = ref(false)
		const execute = async () => {
			error.value = null
			pending.value = true
			data.value = null

			try {
				data.value = await this.request<T>(endpoint, options)
			} catch (e) {
				error.value = e as Error
			} finally {
				pending.value = false
			}
		}

		return {
			data,
			error,
			pending,
			refresh: execute,
			execute
		}
	}
	// API modules
	onboardingV2 = createOnboardingV2Api(this.request.bind(this), this.createApiState.bind(this))

	superAdminRoles = createSuperAdminRolesApi(this.request.bind(this), this.createApiState.bind(this))

	superAdminPermissions = createSuperAdminPermissionsApi(this.request.bind(this), this.createApiState.bind(this))

	superAdminUsers = createSuperAdminUsersApi(this.request.bind(this), this.createApiState.bind(this))

	addAccount = createAddAccountApi(this.request.bind(this))

	pregeneratedFees = createPregeneratedFeesApi(this.request.bind(this), this.createApiState.bind(this))

	accountDiscounts = createAccountDiscountApi(this.request.bind(this), this.createApiState.bind(this))

	deviceTools = createDeviceToolsApi(this.request.bind(this))

	internalDeviceTools = createInternalDeviceToolsApi(this.request.bind(this))

	leads = createLeadsApi(this.request.bind(this))
}

// Create the API client instance
const apiClient = new ApiClient(API_BASE)

export default defineNuxtPlugin(() => {
	return { provide: { api: apiClient } }
})
